import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useCulturesQuery,
  useGetTranslationsByCultureQuery,
} from '@Store/api/translationsSlice';

const STORAGE_KEY = 'selectedCulture';
const DEFAULT_CULTURE = 'en-GB';

interface ICultureContext {
  culture: string;
  setCulture: (cultureCode: string) => void;
  t: (key: string, fallback?: string) => string;
  isLoading: boolean;
}

const CultureContext = createContext<ICultureContext>({
  culture: DEFAULT_CULTURE,
  setCulture: () => {},
  t: (key, fallback) => fallback ?? key,
  isLoading: false,
});

// Matches the browser's preferred languages against the cultures we actually
// have configured, e.g. browser `fr-BE` still resolves to our `fr-FR`.
function resolveBrowserCulture(availableCodes: string[]): string {
  const browserLanguages =
    navigator.languages?.length ? navigator.languages : [navigator.language];

  const exactMatch = browserLanguages
    .map((lang) =>
      availableCodes.find((code) => code.toLowerCase() === lang.toLowerCase())
    )
    .find((match) => !!match);

  if (exactMatch) {
    return exactMatch;
  }

  const partialMatch = browserLanguages
    .map((lang) => {
      const primary = lang.split('-')[0].toLowerCase();

      return availableCodes.find(
        (code) => code.split('-')[0].toLowerCase() === primary
      );
    })
    .find((match) => !!match);

  if (partialMatch) {
    return partialMatch;
  }

  return availableCodes.includes(DEFAULT_CULTURE)
    ? DEFAULT_CULTURE
    : availableCodes[0];
}

interface IProps {
  children: ReactNode;
}

export function CultureProvider({ children }: IProps) {
  const { data: cultures } = useCulturesQuery();

  const [culture, setCultureState] = useState<string>(
    () => localStorage.getItem(STORAGE_KEY) || DEFAULT_CULTURE
  );
  const [hasExplicitPreference] = useState<boolean>(
    () => !!localStorage.getItem(STORAGE_KEY)
  );

  // Only auto-detect from the browser if the user has never explicitly
  // picked a culture from the dropdown.
  useEffect(() => {
    if (hasExplicitPreference || !cultures?.length) {
      return;
    }

    const activeCodes = cultures.filter((c) => c.isActive).map((c) => c.cultureCode);

    if (!activeCodes.length) {
      return;
    }

    setCultureState(resolveBrowserCulture(activeCodes));
  }, [cultures, hasExplicitPreference]);

  const { data: translations, isFetching } = useGetTranslationsByCultureQuery(
    culture,
    { skip: !culture }
  );

  const setCulture = (cultureCode: string) => {
    localStorage.setItem(STORAGE_KEY, cultureCode);
    setCultureState(cultureCode);
  };

  const t = useMemo(
    () => (key: string, fallback?: string) => translations?.[key] ?? fallback ?? key,
    [translations]
  );

  const value = useMemo(
    () => ({ culture, setCulture, t, isLoading: isFetching }),
    [culture, t, isFetching]
  );

  return <CultureContext.Provider value={value}>{children}</CultureContext.Provider>;
}

export function useTranslation() {
  return useContext(CultureContext);
}
